import { api } from './api'

export interface SkillTool {
  name: string
  description?: string
  type?: string
  parameters?: Record<string, any>
}

export interface SkillItem {
  id: string
  name: string
  description?: string
  category?: string
  version?: string
  visibility?: string
  enabled?: boolean
  has_credentials: boolean
  credentials_configured: boolean
  config?: Record<string, any>
  tools?: SkillTool[]
  tags?: string[]
  created_at?: string
}

export interface SkillEnableRequest {
  credentials?: Record<string, any>
  config?: Record<string, any>
}

export interface SkillCreateRequest {
  name: string
  description: string
  content?: string
  frontmatter?: Record<string, any>
  visibility?: string
}

// -- Builder types --

export interface ScriptDef {
  name: string
  description: string
  language: string  // python | bash | node
  code: string
}

export interface CredentialFieldDef {
  name: string
  type: string
  label: string
  required: boolean
}

export interface ConfigFieldDef {
  name: string
  type: string  // string | number | boolean | select
  label: string
  default: string
  options: string[]
}

export interface SkillBuilderRequest {
  name: string
  description: string
  content: string
  category: string
  tags: string[]
  visibility: string
  tools: any[]
  mcp_server?: Record<string, any> | null
  scripts: ScriptDef[]
  credentials: CredentialFieldDef[]
  config: ConfigFieldDef[]
  references: { path: string; content: string }[]
}

export interface SkillFullResponse extends SkillItem {
  content?: string
  tools?: any[]
  scripts?: ScriptDef[]
  credentials_schema?: CredentialFieldDef[]
  config_schema?: ConfigFieldDef[]
  references?: { path: string; content: string }[]
  mcp_server?: Record<string, any> | null
  tags?: string[]
}

export interface SkillAnalyticEntry {
  name: string
  executions: number
  last_used: string
  tools?: string[]
}

export interface SkillAnalytics {
  total_executions: number
  period_days: number
  skills: SkillAnalyticEntry[]
}

export interface SkillLogEntry {
  id: string
  skill_name: string
  script_name: string
  status: 'success' | 'error' | 'timeout'
  error_message?: string
  args_summary?: Record<string, any>
  result_preview?: string
  latency_ms?: number
  created_at: string
}

export interface SkillLogSummary {
  total_runs: number
  error_count: number
  avg_latency_ms: number | null
  error_rate_pct: number
  last_error: string | null
  last_error_at: string | null
}

export interface SkillLogsResponse {
  skill_name: string
  summary: SkillLogSummary
  logs: SkillLogEntry[]
}

export const skillsService = {
  // List all available skills for the current user
  async listAvailable(): Promise<SkillItem[]> {
    const response = await api.get('/skills')
    return response.data
  },

  // List skills for a specific chatbot (shows enabled state)
  async listForBot(chatbotId: string): Promise<SkillItem[]> {
    const response = await api.get(`/chatbots/${chatbotId}/skills`)
    return response.data
  },

  // Enable a skill on a chatbot
  async enable(chatbotId: string, skillId: string, body?: SkillEnableRequest): Promise<any> {
    const response = await api.post(`/chatbots/${chatbotId}/skills/${skillId}/enable`, body || {})
    return response.data
  },

  // Disable a skill on a chatbot
  async disable(chatbotId: string, skillId: string): Promise<void> {
    await api.delete(`/chatbots/${chatbotId}/skills/${skillId}/disable`)
  },

  // Create a custom skill
  async create(body: SkillCreateRequest): Promise<SkillItem> {
    const response = await api.post('/skills', body)
    return response.data
  },

  // Upload skill from zip
  async upload(file: File): Promise<SkillItem> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/skills/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  // Update a custom skill
  async update(skillId: string, body: Partial<SkillCreateRequest>): Promise<SkillItem> {
    const response = await api.put(`/skills/${skillId}`, body)
    return response.data
  },

  // Delete a custom skill
  async delete(skillId: string): Promise<void> {
    await api.delete(`/skills/${skillId}`)
  },

  // -- Builder endpoints --

  // Get full structured skill data for builder form
  async getFullSkill(skillId: string): Promise<SkillFullResponse> {
    const response = await api.get(`/skills/${skillId}/full`)
    return response.data
  },

  // Create skill from builder form
  async createFromBuilder(body: SkillBuilderRequest): Promise<SkillItem> {
    const response = await api.post('/skills/builder', body)
    return response.data
  },

  // Update skill from builder form
  async updateFromBuilder(skillId: string, body: SkillBuilderRequest): Promise<SkillItem> {
    const response = await api.put(`/skills/${skillId}/builder`, body)
    return response.data
  },

  // Get skill execution analytics for a chatbot
  async getAnalytics(chatbotId: string, days?: number): Promise<SkillAnalytics> {
    const response = await api.get(`/chatbots/${chatbotId}/skills/analytics`, { params: { days } })
    return response.data
  },

  // Get execution logs for a specific skill
  async getLogs(chatbotId: string, skillName: string, limit?: number, status?: string): Promise<SkillLogsResponse> {
    const response = await api.get(`/chatbots/${chatbotId}/skills/${encodeURIComponent(skillName)}/logs`, {
      params: { limit, status },
    })
    return response.data
  },
}
